import { ReactElement, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useProject } from 'store/project';

import { CompanyColumnsFactory, CompanyDataGridRow } from './columns';
import { useCompanyDataGridSlotProps } from './useCompanyDataGridSlotProps';
import { EmptyListReason } from 'components/common/noTableContentInfo/NoTableContentInfo';
import { sxStyles } from './styles';
import { useAppNavigation } from 'contexts/navigation';
import { useCompanyColumnGroupingModel } from './useCompanyColumnGroupingModel';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  INITIAL_SORT_MODEL,
} from './constants';
import { DataGridInstanceName } from 'services/storage/keys';
import { SearchPhraseMismatchNoRowsOverlay } from 'components/common/dataGrid/noRowsOverlay';
import { DataGrid } from 'components/common/dataGrid';
import { createClientDataGridConfig } from 'components/common/dataGrid/config/DataGridConfig';

type CompanyDataGridProps = {
  data: CompanyDataGridRow[];
  loading?: boolean;
  emptyListReason?: EmptyListReason;
};

export function CompanyDataGrid({
  data,
  loading = false,
  emptyListReason,
}: CompanyDataGridProps): ReactElement {
  const intl = useIntl();
  const { timezone, organizationId } = useProject();
  const { goToCompanyDetails } = useAppNavigation();

  const columns = useMemo(
    () =>
      new CompanyColumnsFactory(intl, timezone, organizationId).create(),
    [intl, timezone, organizationId]
  );

  const columnGroupingModel = useCompanyColumnGroupingModel();
  const slotProps = useCompanyDataGridSlotProps({ columns });

  const config = useMemo(
    () =>
      createClientDataGridConfig<CompanyDataGridRow>(
        DataGridInstanceName.company,
        INITIAL_SORT_MODEL
      )
        .withPagination({ defaultPageSize: DEFAULT_PAGE_SIZE, pageSizeOptions: PAGE_SIZE_OPTIONS })
        .withRowDoubleClick((params) => {
          if (params.row && params.row._id) {
            goToCompanyDetails(params.row._id);
          }
        })
        .withNoRowsOverlay(SearchPhraseMismatchNoRowsOverlay)
        .withSlotProps(slotProps)
        .withColumnGrouping(columnGroupingModel)
        .withAutosizing()
        .build(),
    [goToCompanyDetails, slotProps, columnGroupingModel]
  );

  return (
    <DataGrid
      data={data}
      columns={columns}
      config={config}
      loading={loading}
      emptyListReason={emptyListReason}
      styles={sxStyles}
    />
  );
}
